/**
 * THE REVIEW (R7, ADR-0014) — a person with review authority acts on one of
 * Leveri's proposed conclusions: Accept, Adjust, Reject, or Request evidence.
 *
 * EVERY WORD ABOUT STATE IS THE SERVER'S. Whether this person may review
 * (`canReview`), the position a conclusion is in (`review.status`, `label`,
 * `tone`), what an act said, who made it, under which authority, and why a
 * later version superseded it — all arrive decided. This file arranges them
 * and sends one act. It contains no branch that decides a professional state.
 *
 * ONE SATURATED ELEMENT. A conclusion offers a quiet "Review" control; opening
 * it shows the four choices, the reviewer's own words (required — they are
 * filed with the act) and one primary "Record review". Only one review is open
 * at a time, so the screen never carries two primaries.
 *
 * THE ACT IS AGAINST THE VERSION ON THE SCREEN. The request names
 * `work.review.versionId`: if a newer version became current meanwhile, the
 * server refuses it as stale and says so, and the person reviews what is
 * current. The answer replaces the file, like every other act here.
 */
import { useState } from "react";
import { WorkActionResponse, type ConclusionRow, type ReviewAct, type WorkFile } from "@leveri/contracts";
import { Button, Field, Notice, Reading, Select, StateText, markerTone } from "../../file/index.js";
import { call, failureMessage } from "../../api.js";
import { useKeyring } from "./keys.js";

/** The four acts and the prompt each puts to the reviewer. Input wording only — never a state. */
const ACTS = [
  { outcome: "ACCEPTED", choice: "Accept", prompt: "Basis for accepting", hint: "Filed with your acceptance." },
  { outcome: "ADJUSTED", choice: "Adjust", prompt: "Basis for your adjusted conclusion", hint: "Filed with your adjustment, beside Leveri's conclusion." },
  { outcome: "REJECTED", choice: "Reject", prompt: "Why you reject it", hint: "Filed with your rejection." },
  { outcome: "EVIDENCE_REQUESTED", choice: "Request evidence", prompt: "What evidence is needed", hint: "Recorded on this conclusion. It does not yet reach the client or restart the work." },
] as const;
type Outcome = (typeof ACTS)[number]["outcome"];

/** One recorded act, as the record states it. */
export function ReviewActLine({ act }: { act: ReviewAct }) {
  return (
    <div className="text-[13px] text-stone">
      <p className="text-ink-soft">
        {act.outcomeLabel} by {act.reviewer} ({act.roleLabel}) · {act.reviewedAtText} · workpaper version {act.versionSeq}
      </p>
      {act.adjustedConclusion && (
        <p className="mt-0.5" title={act.adjustedConclusion.conclusionKind}>
          Adjusted conclusion: {act.adjustedConclusion.label} — {act.adjustedConclusion.text}
        </p>
      )}
      <p className="mt-0.5">{act.basisLabel}: {act.basis}</p>
      <p className="mt-0.5">{act.authorityLabel}.</p>
    </div>
  );
}

/** Where this conclusion stands in review, and the record behind it. */
export function ReviewPosition({ row }: { row: ConclusionRow }) {
  const review = row.review;
  // Nothing recorded and nothing to reconsider: the engine's own "Proposed"
  // line already says it; a second "Proposed" would be noise.
  if (!review.act && !review.previous && !review.supersession) return null;
  return (
    <div className="mt-2" data-testid="review-position">
      <p className="text-[13px]" title={review.status}>
        <StateText tone={markerTone(review.tone)}>{review.label}</StateText>
      </p>
      {review.act && <div className="mt-1"><ReviewActLine act={review.act} /></div>}
      {review.supersession && (
        <p className="mt-1 text-[13px] text-stone">
          Superseded by workpaper version {review.supersession.versionSeq} · {review.supersession.supersededAtText}. {review.supersession.reason}
        </p>
      )}
      {review.previous && (
        <div className="mt-1 border-l-2 border-rule pl-3">
          <p className="text-[13px] text-stone">A person reviewed the conclusion this one replaced:</p>
          <ReviewActLine act={review.previous} />
          <p className="mt-0.5 text-[13px] text-stone">{review.previous.supersession.reason}</p>
        </div>
      )}
    </div>
  );
}

export interface ReviewControlProps {
  work: WorkFile;
  row: ConclusionRow;
  open: boolean;
  onOpen: (conclusionId: string | null) => void;
  onWork: (work: WorkFile) => void;
  onOutcome: (outcome: string) => void;
}

/** The act itself — only where the server says this person may review this conclusion now. */
export function ReviewControl({ work, row, open, onOpen, onWork, onOutcome }: ReviewControlProps) {
  const [outcome, setOutcome] = useState<Outcome>("ACCEPTED");
  const [basis, setBasis] = useState("");
  const [adjustKind, setAdjustKind] = useState("");
  const [adjustText, setAdjustText] = useState("");
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const keyFor = useKeyring();

  if (!row.canReview || !work.review.versionId) return null;
  if (!open) {
    return (
      <div className="mt-2">
        <Button variant="secondary" size="sm" onClick={() => onOpen(row.id)} data-testid={`review-open-${row.id}`}>Review</Button>
      </div>
    );
  }

  const act = ACTS.find((a) => a.outcome === outcome)!;
  const adjusting = outcome === "ADJUSTED";
  const ready = basis.trim().length > 0 && (!adjusting || (adjustKind !== "" && adjustText.trim().length > 0));

  async function record() {
    if (busy || !ready) return;
    setBusy(true);
    setFailure(null);
    try {
      const result = await call("POST", `/api/work/${work.id}/conclusions/${row.id}/review`, WorkActionResponse, {
        artifactVersionId: work.review.versionId,
        outcome,
        basis: basis.trim(),
        ...(adjusting ? { adjustedConclusion: { conclusionKind: adjustKind, conclusionText: adjustText.trim() } } : {}),
        // The act is "this outcome, on this conclusion, against this version".
        // Pressing twice is one act; changing any of the three is another.
        submissionKey: keyFor(`review:${row.id}:${work.review.versionId}:${outcome}:${adjustKind}`),
      });
      onOutcome(result.outcome);
      onWork(result.work);
      onOpen(null);
    } catch (error) {
      setFailure(failureMessage(error));
      setBusy(false);
    }
  }

  return (
    <div className="mt-3 border-t border-rule-soft pt-3" data-testid={`review-form-${row.id}`}>
      <div role="radiogroup" aria-label="Your review" className="flex flex-wrap gap-x-5 gap-y-2">
        {ACTS.map((a) => (
          <label key={a.outcome} className="inline-flex cursor-pointer items-center gap-2 text-sm">
            <input type="radio" name={`review-${row.id}`} className="accent-forest" disabled={busy}
              checked={outcome === a.outcome} onChange={() => setOutcome(a.outcome)} />
            {a.choice}
          </label>
        ))}
      </div>

      {adjusting && (
        <div className="mt-3 grid gap-3">
          <Select label="Your conclusion" value={adjustKind} disabled={busy} onChange={(e) => setAdjustKind(e.target.value)}>
            <option value="">Choose</option>
            {row.adjustOptions.map((o) => <option key={o.conclusionKind} value={o.conclusionKind}>{o.label}</option>)}
          </Select>
          <Field label="Your conclusion, in words">
            <textarea rows={2} value={adjustText} disabled={busy} onChange={(e) => setAdjustText(e.target.value)}
              className="w-full rounded-control border border-line-strong bg-canvas px-3 py-2 text-sm text-ink focus:border-brand" />
          </Field>
        </div>
      )}

      <div className="mt-3">
        <Field label={act.prompt} hint={act.hint}>
          <textarea rows={2} value={basis} disabled={busy} onChange={(e) => setBasis(e.target.value)}
            className="w-full rounded-control border border-line-strong bg-canvas px-3 py-2 text-sm text-ink focus:border-brand" />
        </Field>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-3.5">
        <Button disabled={busy || !ready} onClick={() => void record()}>Record review</Button>
        <Button variant="ghost" disabled={busy} onClick={() => onOpen(null)}>Cancel</Button>
      </div>
      <p className="mt-2 text-[13px] text-stone">
        Recorded against workpaper version {work.review.versionSeq}, in your name. Leveri&rsquo;s conclusion itself is not changed.
      </p>
      {busy && <div className="mt-2"><Reading>Recording your review…</Reading></div>}
      {failure && <div className="mt-2"><Notice>{failure}</Notice></div>}
    </div>
  );
}
